/**
 * Time maths for the schedule time stepper (the − / + buttons and the
 * typed-time field on the producer's phone).
 *
 * Buttons: shiftRange / adjustStart / adjustEnd take a step in minutes.
 * Typed field: parseTypedTime reads the text, then moveStartTo / moveEndTo
 * apply that clock time through the same checks, so a typed time and a
 * button tap can never disagree about what is allowed.
 *
 * Schedule times are wall-clock "HH:MM" without a date — the DB's `time`
 * columns come back as "HH:MM:SS". Two conventions give them meaning, the
 * same two effectiveShift.ts and the SQL helpers (migration 20260524120000)
 * apply:
 *   - end < start means the shift crosses midnight (23:00 → 01:00);
 *   - a start before FESTIVAL_DAY_ROLLOVER_HOUR (06:00) belongs to the
 *     previous festival night, so it happens on event_date + 1.
 *
 * So the stepper does its sums on the festival-day axis (06:00 → 05:59, with
 * after-midnight hours rolled +24h — the same idea as the web timeline's
 * toTimelineMinutes in apps/web/src/lib/resizeAssignmentTime.ts), never on
 * raw clock minutes. On that axis 01:00 sits after 23:00, a midnight-crossing
 * shift is just a range, and a move that would change which festival night a
 * shift belongs to is easy to spot — and refused: 06:00 − 5 min reads as
 * 05:55, which the convention files under the NEXT calendar day, so the shift
 * would silently jump a whole day while the screen showed a five-minute nudge.
 */

import { shiftDurationMinutes } from "./effectiveShift";
import { FESTIVAL_DAY_ROLLOVER_HOUR } from "./eventDays";

/** One tap on the − / + buttons. */
export const STEP_MINUTES = 5;

/**
 * Shortest assignment the stepper will produce. The web timeline's
 * drag-resize (apps/web/src/lib/resizeAssignmentTime.ts) imports this same
 * constant, so a shift the phone accepts can also be resized on the web and
 * vice versa.
 */
export const MIN_ASSIGNMENT_MINUTES = 15;

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** 06:00 on the festival-day axis — where a festival day begins … */
const FESTIVAL_DAY_START = FESTIVAL_DAY_ROLLOVER_HOUR * MINUTES_PER_HOUR;
/** … and 06:00 the next morning, where the next one begins (exclusive). */
const FESTIVAL_DAY_END = FESTIVAL_DAY_START + MINUTES_PER_DAY;

/** A shift's wall-clock window, "HH:MM" or "HH:MM:SS". */
export interface ShiftTimes {
  start: string;
  end: string;
}

/**
 * Why the stepper refused a move. The screen turns this into a message; it
 * never applies a refused move partially.
 *
 * - `crosses-festival-day`: the start would pass 06:00 and so land on another
 *   festival night.
 * - `too-short`: the shift would drop below MIN_ASSIGNMENT_MINUTES.
 * - `too-long`: the shift would reach 24 hours. A wall-clock end can't express
 *   that — the end would wrap past the start and read as a short shift.
 * - `invalid-time`: a stored time (or the step) isn't a usable value.
 */
export type TimeStepRefusal = "crosses-festival-day" | "too-short" | "too-long" | "invalid-time";

/** Outcome of a stepper move: both new times as "HH:MM", or why not. */
export type TimeStepResult =
  | { ok: true; start: string; end: string }
  | { ok: false; reason: TimeStepRefusal };

/** A shift placed on the festival-day axis, in minutes. */
interface AxisShift {
  startAt: number;
  endAt: number;
}

/** Stored-value form: "H:MM", "HH:MM" or the DB's "HH:MM:SS". */
const STORED_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/** Minutes since midnight, or null when the parts aren't a real clock time. */
function minutesOfDay(hours: number, minutes: number, seconds = 0): number | null {
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return hours * MINUTES_PER_HOUR + minutes;
}

/**
 * Minutes since midnight for a stored time. Seconds are dropped: the
 * schedule works in whole minutes, and keeping them would leave "21:30:45"
 * a minute adrift of the "21:30" the screen shows.
 */
function clockMinutes(time: string | null | undefined): number | null {
  if (typeof time !== "string") return null;
  const match = STORED_TIME.exec(time);
  if (!match) return null;
  const [, hours, minutes, seconds = "0"] = match;
  return minutesOfDay(Number(hours), Number(minutes), Number(seconds));
}

/** Clock minutes onto the festival-day axis: before 06:00 rolls +24h. */
function toFestivalAxis(clock: number): number {
  return clock < FESTIVAL_DAY_START ? clock + MINUTES_PER_DAY : clock;
}

/** Any minute count back to wall-clock "HH:MM", wrapped to 24h. */
function formatClock(minutes: number): string {
  const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(wrapped / MINUTES_PER_HOUR);
  const rest = wrapped % MINUTES_PER_HOUR;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/** The step as whole minutes, or null when it isn't a finite number. */
function wholeMinutes(delta: number): number | null {
  const rounded = Math.round(delta);
  return Number.isFinite(rounded) ? rounded : null;
}

/**
 * Place a stored shift on the festival-day axis. The end is the start plus
 * the shared midnight-aware duration (shiftDurationMinutes), so 23:00 → 01:00
 * becomes one continuous 120-minute range, exactly as effectiveShiftEnd
 * reads it.
 */
function toAxisShift(shift: ShiftTimes): AxisShift | null {
  const start = clockMinutes(shift.start);
  if (start === null || clockMinutes(shift.end) === null) return null;
  const startAt = toFestivalAxis(start);
  return { startAt, endAt: startAt + shiftDurationMinutes(shift.start, shift.end) };
}

function refuse(reason: TimeStepRefusal): TimeStepResult {
  return { ok: false, reason };
}

function accept({ startAt, endAt }: AxisShift): TimeStepResult {
  return { ok: true, start: formatClock(startAt), end: formatClock(endAt) };
}

/** Would a start at this axis position belong to another festival day? */
function leavesFestivalDay(startAt: number): boolean {
  return startAt < FESTIVAL_DAY_START || startAt >= FESTIVAL_DAY_END;
}

/**
 * Length check after a move. A shift already under the minimum (older data,
 * or a time typed in elsewhere) may still GROW: refusing every tap would
 * leave the + button a dead end. It just may not get shorter. The same rule
 * lets shiftRange through untouched, since moving keeps the length.
 */
function lengthRefusal(before: AxisShift, after: AxisShift): TimeStepRefusal | null {
  const duration = after.endAt - after.startAt;
  if (duration >= MINUTES_PER_DAY) return "too-long";
  if (duration < MIN_ASSIGNMENT_MINUTES && duration < before.endAt - before.startAt) {
    return "too-short";
  }
  return null;
}

/**
 * Shared shape of the three moves: parse, move, check, format. `move` gets
 * the shift on the festival-day axis and the step in whole minutes. The
 * festival-day check comes first: another night is the bigger mistake.
 */
function stepShift(
  shift: ShiftTimes,
  deltaMinutes: number,
  move: (axis: AxisShift, delta: number) => AxisShift,
): TimeStepResult {
  const before = toAxisShift(shift);
  const delta = wholeMinutes(deltaMinutes);
  if (before === null || delta === null) return refuse("invalid-time");
  const after = move(before, delta);
  if (leavesFestivalDay(after.startAt)) return refuse("crosses-festival-day");
  const tooShortOrLong = lengthRefusal(before, after);
  return tooShortOrLong ? refuse(tooShortOrLong) : accept(after);
}

/**
 * Step a single wall-clock time by `deltaMinutes`, wrapping at 24h:
 * ("23:50", 15) → "00:05". Accepts the DB's "HH:MM:SS" and always returns
 * "HH:MM". Knows nothing about shifts or festival days — use shiftRange /
 * adjustStart / adjustEnd to move a shift. Returns null when `time` isn't a
 * valid clock time or the step isn't a finite number.
 */
export function stepTime(time: string, deltaMinutes: number): string | null {
  const clock = clockMinutes(time);
  const delta = wholeMinutes(deltaMinutes);
  if (clock === null || delta === null) return null;
  return formatClock(clock + delta);
}

/**
 * Move the whole shift — start and end together — keeping its length.
 * 23:00 → 01:00 moved +60 becomes 00:00 → 02:00.
 *
 * Refused (`crosses-festival-day`) when the START would pass 06:00 in either
 * direction, because that silently files the shift under another festival
 * night. The end may pass 06:00: a night shift 22:00 → 07:00 is normal.
 * Never refused for length — moving doesn't change it — so even a shift
 * already shorter than the minimum still moves.
 */
export function shiftRange(shift: ShiftTimes, deltaMinutes: number): TimeStepResult {
  return stepShift(shift, deltaMinutes, ({ startAt, endAt }, delta) => ({
    startAt: startAt + delta,
    endAt: endAt + delta,
  }));
}

/**
 * Move only the start; the end stays put. Refused when the start would pass
 * 06:00 (`crosses-festival-day`), when the shift would drop below
 * MIN_ASSIGNMENT_MINUTES (`too-short`) or reach 24 hours (`too-long`).
 */
export function adjustStart(shift: ShiftTimes, deltaMinutes: number): TimeStepResult {
  return stepShift(shift, deltaMinutes, ({ startAt, endAt }, delta) => ({
    startAt: startAt + delta,
    endAt,
  }));
}

/**
 * Move only the end; the start stays put, so the shift never changes
 * festival night. The end may cross midnight (23:50 + 15 → 00:05) or 06:00.
 * Refused when the shift would drop below MIN_ASSIGNMENT_MINUTES
 * (`too-short`) or reach 24 hours (`too-long`).
 */
export function adjustEnd(shift: ShiftTimes, deltaMinutes: number): TimeStepResult {
  return stepShift(shift, deltaMinutes, ({ startAt, endAt }, delta) => ({
    startAt,
    endAt: endAt + delta,
  }));
}

/**
 * Does the shift end on the calendar day after it starts? Drives the "+1"
 * badge next to the end time.
 *
 * True exactly when end < start on the wall clock (23:50 → 00:05). An end
 * EQUAL to the start is a zero-length shift on the same day, not a 24-hour
 * one: that is how effectiveShiftEnd and the SQL effective_shift_end_at read
 * it, and the badge must agree with the time the rest of the system uses.
 * False for unparseable times.
 */
export function endsNextDay(shift: ShiftTimes): boolean {
  const start = clockMinutes(shift.start);
  const end = clockMinutes(shift.end);
  if (start === null || end === null) return false;
  return end < start;
}

/**
 * Would the producer have meant this typed start as the morning BEFORE this
 * festival day, rather than as the end of this night?
 *
 * The convention files a start before 06:00 at the end of the night (event
 * date + 1), and moveStartTo reads it that way. But typing 05:55 on a 06:00
 * shift means "five minutes earlier", and that morning belongs to the
 * previous festival night. Only the refusal reason depends on this: it is
 * asked only when the end-of-night reading would put the start at or past
 * the end, which is refused anyway. The nearer reading wins, so 04:00 typed
 * on a 23:00 → 03:00 shift stays "too-short" (an hour after the end, same
 * night), while 05:55 on 06:00 → 08:00 says "crosses-festival-day".
 */
function meantPreviousMorning(clock: number, before: AxisShift): boolean {
  if (clock >= FESTIVAL_DAY_START) return false;
  const endOfNight = toFestivalAxis(clock);
  if (endOfNight < before.endAt) return false;
  return before.startAt - clock < endOfNight - before.startAt;
}

/**
 * Set the start to a clock time (e.g. parseTypedTime's result); the end stays
 * put. The time is placed on the shift's own festival night, so 23:00 typed on
 * a 01:00 → 03:00 shift is two hours earlier, not 22 hours later. The move
 * then goes through adjustStart, with the same refusals. A typed time before
 * 06:00 that only fits as the morning before this festival day is refused as
 * `crosses-festival-day` (see meantPreviousMorning). Takes "HH:MM" or
 * "HH:MM:SS"; anything else, including null, is `invalid-time`.
 */
export function moveStartTo(shift: ShiftTimes, time: string | null | undefined): TimeStepResult {
  const before = toAxisShift(shift);
  const clock = clockMinutes(time);
  if (before === null || clock === null) return refuse("invalid-time");
  if (meantPreviousMorning(clock, before)) return refuse("crosses-festival-day");
  return adjustStart(shift, toFestivalAxis(clock) - before.startAt);
}

/**
 * Set the end to a clock time (e.g. parseTypedTime's result); the start stays
 * put. An end before the start on the clock is the next day, as everywhere
 * else (shiftDurationMinutes), so 01:00 typed on a 22:00 → 23:00 shift makes
 * it 22:00 → 01:00. The move then goes through adjustEnd, with the same
 * refusals. Takes "HH:MM" or "HH:MM:SS"; anything else, including null, is
 * `invalid-time`.
 */
export function moveEndTo(shift: ShiftTimes, time: string | null | undefined): TimeStepResult {
  const before = toAxisShift(shift);
  const after = toAxisShift({ start: shift.start, end: time ?? "" });
  if (before === null || after === null) return refuse("invalid-time");
  return adjustEnd(shift, after.endAt - before.endAt);
}

/**
 * The producer's time-format setting (schedule_time_format). Only "12h"
 * changes anything; any other value, or none, is 24h — the same rule
 * formatTimeWithSetting uses to show times, so typing and display agree.
 */
export type TypedTimeFormat = "24h" | "12h" | string | null | undefined;

/** "9pm", "9 PM", "9.30 p.m." — body, then an am/pm marker. */
const MERIDIEM = /^(.*?)\s*([ap])\.?m\.?$/;
/** "21:30", "9:30", "21.30" (Dutch notation), "21:30:00" (a pasted DB value). */
const SEPARATED = /^(\d{1,2})[:.](\d{2})(?::(\d{2}))?$/;
/** "2130", "930" — the last two digits are the minutes. */
const DIGITS_ONLY = /^(\d{1,2})(\d{2})$/;
/** "21", "9" — a whole hour. */
const HOUR_ONLY = /^(\d{1,2})$/;

/** 12-hour clock to 24-hour: 12am → 0, 12pm → 12, 9pm → 21. Null off 1–12. */
function from12Hour(hours: number, marker: string): number | null {
  if (hours < 1 || hours > 12) return null;
  return (hours % 12) + (marker === "p" ? 12 : 0);
}

/**
 * Hours of a time typed WITHOUT am/pm. On the 12h setting 1–12 could be
 * morning or evening ("9:30": 09:30 or 21:30?), so they're refused, not
 * guessed. 0 and 13–23 can only be a 24-hour time, so they stand.
 */
function unmarkedHours(hours: number, timeFormat: TypedTimeFormat): number | null {
  const ambiguous = timeFormat === "12h" && hours >= 1 && hours <= 12;
  return ambiguous ? null : hours;
}

/** Split a typed time body into hour / minute / second numbers, or null. */
function splitTyped(body: string): [number, number, number] | null {
  const match = SEPARATED.exec(body) ?? DIGITS_ONLY.exec(body) ?? HOUR_ONLY.exec(body);
  if (!match) return null;
  const [, hours, minutes = "0", seconds = "0"] = match;
  return [Number(hours), Number(minutes), Number(seconds)];
}

/**
 * Read a time the producer typed, as "HH:MM", or null when it can't be read
 * without guessing. Pass the producer's time-format setting: what "9:30"
 * means depends on it.
 *
 * Friendly: surrounding spaces are ignored, and "21:30", "21.30", "2130",
 * "930" (→ 09:30), "21" (→ 21:00), "9:30" and a pasted "21:30:00" all work,
 * as do entries with am/pm ("9:30 pm", "9pm", "12am" → 00:00) on either
 * setting.
 *
 * Strict: anything that needs a guess is refused instead of silently turned
 * into a wrong shift time — "9:5" (09:05 or 09:50?), "24:00" (type 00:00),
 * "21:60", "13pm", "0am", five or more digits, and any other text. On the
 * 12h setting an hour of 1–12 without am/pm is refused too ("9:30" → null,
 * "9:30 pm" → 21:30), while "21:30" or "00:30" are still read: they can only
 * be 24-hour times.
 *
 * Typed times are not snapped to STEP_MINUTES: typing is how a producer
 * enters an exact time the buttons don't reach.
 */
export function parseTypedTime(
  input: string | null | undefined,
  timeFormat: TypedTimeFormat = "24h",
): string | null {
  if (typeof input !== "string") return null;
  const text = input.trim().toLowerCase();
  const meridiem = MERIDIEM.exec(text);
  const parts = splitTyped(meridiem ? meridiem[1] : text);
  if (!parts) return null;

  const [typedHours, minutes, seconds] = parts;
  const hours = meridiem
    ? from12Hour(typedHours, meridiem[2])
    : unmarkedHours(typedHours, timeFormat);
  if (hours === null) return null;
  const clock = minutesOfDay(hours, minutes, seconds);
  return clock === null ? null : formatClock(clock);
}
